import React from 'react'

import { Badge, Box, Button, DoubleRangeSlider, Text } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { RatingDefaultValues, setLabels } from 'modules/questionnaires/lib/defaultRange'
import { Scale } from 'modules/questionnaires/lib/types'

type RatingModalTranslations = {
  scoring_range: string
  scale_title: string
  scale_description: string
  cancel: string
  add: string
  range_title: string
  range_description: string
}

export type HandleSubmitRating = {
  submitting?: boolean
  onSubmit: (scales: Scale[]) => Promise<{ success: boolean }>
}

type Props = {
  translations: RatingModalTranslations
  defaultRatingScale: Scale[]
  handleSubmit: HandleSubmitRating
  onClose: () => void
}

const EditRatingModal: React.FC<Props> = ({
  translations,
  defaultRatingScale,
  handleSubmit,
  onClose,
}) => {
  const { submitting = false, onSubmit } = handleSubmit
  const rangeDefault = defaultRatingScale.map((r) => r.value)
  const [defaultRange, setDefaultRange] = React.useState<{
    min: number
    max: number
    labels: { value: number; text: string }[]
  }>({
    min: Math.min(...rangeDefault),
    max: Math.max(...rangeDefault),
    labels: setLabels(defaultRatingScale),
  })

  const onSelectRange = ([min, max]: [number, number]) => {
    const minValue = Math.round(min)
    const maxValue = Math.round(max)

    if (minValue === maxValue) return

    setDefaultRange({ ...defaultRange, min: minValue, max: maxValue })
  }

  const updateRatingValueTitle = (
    newTextString: string,
    objectToShow: {
      value: number
      text: string
    }
  ) => {
    const updatedRating = defaultRange.labels.find((item) => item.value === objectToShow.value) || {
      value: objectToShow.value,
      text: '',
    }
    updatedRating.text = newTextString
    setDefaultRange({
      ...defaultRange,
    })
  }

  const objectsToShow = defaultRange.labels.filter((item) => {
    return item?.value >= defaultRange.min && item.value <= defaultRange.max
  })

  const isSubmittingDisabled = Boolean(
    !objectsToShow[0]?.text || !objectsToShow[objectsToShow.length - 1]?.text
  )

  const handleOnClick = async () => {
    const result = await onSubmit(objectsToShow)
    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onClose()
    }
  }

  return (
    <Modal
      illustration="page-edit"
      title={translations.scoring_range}
      onClose={onClose}
      footer={
        <Box justifyContent="center" alignItems="center" flexDirection="row" gap="s8">
          <Button type="secondary" onClick={onClose} disabled={submitting}>
            {translations.cancel}
          </Button>
          <Button disabled={isSubmittingDisabled || submitting} onClick={handleOnClick}>
            {translations.add}
          </Button>
        </Box>
      }
    >
      <Box gap="s32">
        <Box gap="s8">
          <Text.Normal size="300" weight="semibold" color="grey1000">
            {translations.scale_title}
          </Text.Normal>
          <Box gap="s24" paddingBottom="s32">
            <Text.Normal>{translations.scale_description}</Text.Normal>
            <DoubleRangeSlider
              min={RatingDefaultValues.minValue}
              max={RatingDefaultValues.maxValue}
              value={[defaultRange.min, defaultRange.max]}
              onChange={onSelectRange}
            />
          </Box>
        </Box>
        <Box gap="s8">
          <Text.Normal size="300" weight="semibold" color="grey1000">
            {translations.range_title}
          </Text.Normal>
          <Box gap="s24">
            <Text.Normal>{translations.range_description}</Text.Normal>
            <Box gap="s16">
              {objectsToShow.map((objectToShow) => (
                <Box flexDirection="row" alignItems="center" gap="s8" key={objectToShow.value}>
                  <Badge color="viridian100">
                    <Text.Normal size="200" weight="semibold" color="viridian1000">
                      {objectToShow.value}
                    </Text.Normal>
                  </Badge>
                  <TextField
                    onChange={(value) => updateRatingValueTitle(value, objectToShow)}
                    value={objectToShow.text}
                    mandatory={
                      objectToShow.value === defaultRange.min ||
                      objectToShow.value === defaultRange.max
                    }
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default EditRatingModal
